import { useState } from 'react';
import { Snowflake, List, Calculator, Trophy, Upload } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DebtForm } from '@/components/snowball/DebtForm';
import { DebtCard } from '@/components/snowball/DebtCard';
import { SnowballProjection } from '@/components/snowball/SnowballProjection';
import { SnowballSummary } from '@/components/snowball/SnowballSummary';
import { DataImportDialog } from '@/components/import/DataImportDialog';
import { useDebts, DebtFormData } from '@/hooks/useDebts';
import { useProfile } from '@/hooks/useProfile';
import { sortBySnowball } from '@/lib/snowball-calculations';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { PremiumPageGate } from '@/components/subscription/PremiumPageGate';

export default function Snowball() {
  const {
    debts,
    activeDebts,
    paidOffDebts,
    isLoading,
    createDebt,
    updateDebt,
    deleteDebt,
    markAsPaidOff,
    bulkCreateDebts,
    totalDebt,
    totalPaid,
    progressPercentage,
    totalMinimumPayment,
  } = useDebts();
  const { profile } = useProfile();
  const { toast } = useToast();
  const currency = profile?.default_currency || 'CRC';

  const sortedActiveDebts = sortBySnowball(activeDebts);

  const handleImportDebts = async (data: Record<string, unknown>[]) => {
    await bulkCreateDebts.mutateAsync(data as unknown as DebtFormData[]);
    toast({
      title: 'Importación exitosa',
      description: `Se importaron ${data.length} deudas correctamente`,
    });
  };

  if (isLoading) {
    return (
      <PremiumPageGate
        feature="snowball"
        title="Bola de nieve"
        description="Gestiona y liquida tus deudas con el método bola de nieve — función Premium."
      >
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </AppLayout>
      </PremiumPageGate>
    );
  }

  return (
    <PremiumPageGate
      feature="snowball"
      title="Bola de nieve"
      description="Gestiona y liquida tus deudas con el método bola de nieve — función Premium."
    >
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Snowflake className="h-8 w-8 text-primary" />
              Método Bola de Nieve
            </h1>
            <p className="text-muted-foreground mt-1">
              Paga tus deudas de menor a mayor y gana impulso con cada victoria
            </p>
          </div>
          <div className="flex gap-2">
            <DataImportDialog
              type="debts"
              onImport={handleImportDebts}
              trigger={
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                </Button>
              }
            />
            <DebtForm
              onSubmit={(data) => createDebt.mutate(data)}
              isLoading={createDebt.isPending}
            />
          </div>
        </div>

        {/* Info Card - Only show if no debts */}
        {debts.length === 0 && (
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Snowflake className="h-5 w-5" />
                ¿Qué es el Método Bola de Nieve?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                El método bola de nieve es una estrategia para salir de deudas que consiste en:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                <li><strong>Ordenar</strong> tus deudas de menor a mayor saldo</li>
                <li><strong>Pagar el mínimo</strong> en todas las deudas</li>
                <li><strong>Destinar todo el extra</strong> a la deuda más pequeña</li>
                <li>Cuando la pagas, <strong>"ruedas"</strong> ese pago a la siguiente</li>
              </ol>
              <p className="text-sm text-primary font-medium">
                💡 Las victorias rápidas te mantienen motivado y el "efecto bola de nieve" 
                acelera el pago de cada deuda sucesiva.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        {debts.length > 0 && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left Column - Summary */}
            <div className="lg:col-span-1">
              <SnowballSummary
                totalDebt={totalDebt}
                totalPaid={totalPaid}
                progressPercentage={progressPercentage}
                totalMinimumPayment={totalMinimumPayment}
                activeDebtsCount={activeDebts.length}
                paidOffDebtsCount={paidOffDebts.length}
                currency={currency}
              />
            </div>

            {/* Right Column - Tabs */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="debts" className="space-y-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="debts" className="gap-2">
                    <List className="h-4 w-4" />
                    <span className="hidden sm:inline">Mis Deudas</span>
                  </TabsTrigger>
                  <TabsTrigger value="projection" className="gap-2">
                    <Calculator className="h-4 w-4" />
                    <span className="hidden sm:inline">Proyección</span>
                  </TabsTrigger>
                  <TabsTrigger value="paid" className="gap-2">
                    <Trophy className="h-4 w-4" />
                    <span className="hidden sm:inline">Pagadas</span>
                  </TabsTrigger>
                </TabsList>

                {/* Active Debts Tab */}
                <TabsContent value="debts" className="space-y-4">
                  {sortedActiveDebts.length === 0 ? (
                    <Card>
                      <CardContent className="pt-6 text-center">
                        <p className="text-muted-foreground py-8">
                          No tienes deudas activas. ¡Agrega una para comenzar!
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {sortedActiveDebts.map((debt, index) => (
                        <DebtCard
                          key={debt.id}
                          debt={debt}
                          order={index + 1}
                          isTarget={index === 0}
                          onMarkPaid={(id) => markAsPaidOff.mutate(id)}
                          onDelete={(id) => deleteDebt.mutate(id)}
                          onUpdate={(data) => updateDebt.mutate(data)}
                          currency={currency}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Projection Tab */}
                <TabsContent value="projection">
                  <SnowballProjection debts={debts} currency={currency} />
                </TabsContent>

                {/* Paid Off Tab */}
                <TabsContent value="paid" className="space-y-4">
                  {paidOffDebts.length === 0 ? (
                    <Card>
                      <CardContent className="pt-6 text-center">
                        <div className="py-8">
                          <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground">
                            Aún no has liquidado ninguna deuda.
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            ¡Sigue adelante! Cada pago te acerca a tu libertad financiera.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      <Card className="bg-green-500/5 border-green-500/20">
                        <CardContent className="pt-6 text-center">
                          <Trophy className="h-10 w-10 mx-auto text-green-500 mb-2" />
                          <p className="text-2xl font-bold text-green-600">
                            ¡{paidOffDebts.length} deuda{paidOffDebts.length !== 1 ? 's' : ''} liquidada{paidOffDebts.length !== 1 ? 's' : ''}!
                          </p>
                          <p className="text-muted-foreground text-sm mt-1">
                            Cada victoria cuenta. ¡Sigue así!
                          </p>
                        </CardContent>
                      </Card>
                      {paidOffDebts.map((debt) => (
                        <DebtCard
                          key={debt.id}
                          debt={debt}
                          onMarkPaid={() => {}}
                          onDelete={(id) => deleteDebt.mutate(id)}
                          onUpdate={(data) => updateDebt.mutate(data)}
                          currency={currency}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
    </PremiumPageGate>
  );
}
